/**
 * 화면 — 대시보드 · 화면 목록 · 화면 상세 · 바로 보내기 · 모니터링.
 */
import { Send, SquareMousePointer } from 'lucide-react';
import type { ManualChapter } from '../types.js';

export const DASHBOARD: ManualChapter = {
  slug: 'dashboard',
  title: '대시보드',
  summary: '콘솔을 열자마자 지금 문제가 있는지, 어느 화면이 문제인지 한눈에 확인합니다.',
  menuPath: '/',
  sections: [
    {
      id: 'cards',
      title: '운영 요약 — 숫자 카드 네 개',
      blocks: [
        {
          kind: 'table',
          header: ['카드', '무엇을 세나', '누르면'],
          rows: [
            ['온라인 화면', '지금 켜져 있는 화면 수 / 등록된 전체 화면 수. 아래 줄에 “등록된 화면 N대” 와, 숨긴 화면이 있으면 “숨김 N” 이 함께 적힙니다.', '**켜진 화면만** 남긴 화면 목록'],
            ['활성 콘텐츠', '보관하지 않은 콘텐츠 수', '콘텐츠'],
            ['플레이리스트 · 스케줄', '만들어 둔 플레이리스트와 스케줄 수', '플레이리스트'],
            ['주의 필요', '꺼져 있거나 오류를 알린 화면 수(같은 화면을 두 번 세지 않습니다). 아래 줄에 “오프라인 · 오류 · 정비 모드 · 미배정” 이 나뉘어 적힙니다.', '**그 화면들만** 남긴 화면 목록'],
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '“주의 필요” 가 “모두 정상” 이면 조치할 화면이 없습니다',
          text: '빨간 숫자가 보이면 카드를 누르세요 — **그 화면들만 남긴 목록**이 열려, 40대짜리 목록에서 직접 찾지 않아도 됩니다. 오류를 알린 화면은 해당하는 필터가 없어 전체 목록으로 갑니다. 화면 숫자는 **30초마다** 자동으로 갱신됩니다.',
        },
      ],
    },
    {
      id: 'edit',
      title: '대시보드 구성 바꾸기',
      blocks: [
        {
          kind: 'text',
          text: '오른쪽 위 [[편집]] 을 누르면 대시보드에 올릴 수 있는 **카드 목록**이 나옵니다. 체크하면 그 자리에서 화면에 추가되고, 체크를 풀면 빠집니다. 필요한 정보는 담당에 따라 다릅니다 — 현장 담당자는 꺼진 화면을, 광고 담당자는 재생 실적을 봅니다. 무엇을 어떤 크기로 어디에 둘지는 각자 정합니다.',
        },
        {
          kind: 'actions',
          title: '고를 수 있는 카드',
          items: [
            { label: '운영 요약', text: '맨 위의 숫자 카드 네 개.' },
            { label: '오늘 재생 실적', text: '오늘 몇 번 재생됐는지와 어제 같은 시각 대비 증감. **증빙이 15분 넘게 들어오지 않으면 빨간 경보**가 뜹니다. ‘리포트 조회’ 권한이 필요합니다.' },
            { label: '위치별 화면 현황', text: '위치마다 켜진·꺼진 화면 비율. 위치가 둘 이상일 때만 보입니다.' },
            { label: '화면 현황', text: '문제가 큰 순서로 여덟 대.' },
            { label: '곧 끝나는 콘텐츠', text: '7일 안에 **게시 기간**이 끝나는 콘텐츠 여덟 개와 남은 날(D-3 종료 등). [[모두 보기]] 는 그 콘텐츠만 거른 콘텐츠 목록입니다. 계약이 끝나는 광고를 미리 챙길 때 봅니다.' },
            { label: '콘텐츠 유형', text: '이미지·영상·웹·템플릿 개수.' },
            { label: '공개된 기능', text: '공급사가 이 서버에 공개한 선택 기능이 켜져 있는지와 마지막 결과. 공개한 기능이 없으면 “공개된 선택 기능이 없습니다” 라고만 나옵니다. **처음에는 판에 올라와 있지 않으니** 이 목록에서 골라 올립니다.' },
            { label: '최근 활동', text: '누가 무엇을 바꿨는지 여덟 건. ‘감사로그 조회’ 권한이 필요합니다.' },
          ],
        },
        {
          kind: 'steps',
          title: '자리와 크기 바꾸기 (편집 중)',
          steps: [
            { title: '카드를 잡고 끌어 원하는 자리에 놓습니다.', body: '편집을 켜면 카드마다 점선 테두리가 생기고, 왼쪽 위에 **⠿ 카드 이름** 이 붙습니다. 카드 어디를 잡아도 끌 수 있고, 카드 사이의 틈이나 마지막 줄 아래에도 놓을 수 있습니다. 끄는 동안 다른 카드가 비켜서며 놓일 자리를 보여 줍니다. Esc 를 누르면 취소됩니다. 휴대폰에서는 잠시 누르고 있다가 끕니다. 키보드만 쓸 때는 ⠿ 이름표에 초점을 두고 **Alt + ← →** 로 옮깁니다.' },
            { title: '카드 **오른쪽 아래 모서리**를 끌어 폭(1~3칸)과 높이를 정합니다.', body: '대시보드는 세 칸 격자이고, 옆으로 끌어 간 폭에서 가장 가까운 칸 수에 맞춰집니다. 넓은 표는 3칸, 도넛 그림 하나는 1칸이 읽기 좋습니다. 창이 좁아지면 칸 수도 함께 줄어, 휴대폰에서는 카드가 한 줄에 하나씩 배치됩니다. **아래위로 끌면 그 카드의 높이**가 20px 걸음으로 바뀝니다(120~1600px) — 이웃 카드와 끝을 맞추거나, 긴 목록을 짧게 줄여 카드 안에서 굴려 볼 때 씁니다(카드 이름 줄은 위에 남습니다). 모서리 옆에 지금 칸 수와 높이가 적히고, 높이를 정한 카드는 왼쪽 아래 [[높이 자동]] 으로 내용 높이로 되돌립니다. 키보드로는 이름표에 초점을 두고 **Alt + ↑ ↓** 로 높이를 바꿉니다.' },
            { title: '카드 오른쪽 위의 **×** 를 누르면 그 카드만 대시보드에서 내려갑니다.' },
            { title: '다 됐으면 [[편집 완료]] 를 누릅니다.', body: '따로 저장 단추는 없습니다. 바꾸는 즉시 내 계정에 저장됩니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '이 배치는 내 계정에만 저장됩니다',
          text: '같은 화면을 보는 다른 사람의 대시보드는 그대로입니다. 반대로 내 배치는 **다른 PC 에서 로그인해도 그대로 적용됩니다.** 처음 상태로 되돌리려면 편집 상자의 [[기본값으로]] 를 누르세요. 권한이 없는 카드(예: 감사로그 권한이 필요한 “최근 활동”)는 목록에 나오지 않습니다.',
        },
      ],
    },
    {
      id: 'checklist',
      title: '시작하기 체크리스트',
      blocks: [
        {
          kind: 'text',
          text: '처음 쓰는 동안 맨 위에 “시작하기” 상자가 보입니다. **화면 연결하기 → 콘텐츠 올리기 → 플레이리스트 만들기 → 화면에 편성 배정** 네 단계이고, 누르면 그 메뉴로 갑니다. 실제로 완료하면 자동으로 체크되고, 네 단계가 모두 끝나면 상자가 사라집니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '오른쪽 위 X 로 닫을 수도 있습니다',
          text: '닫았다는 사실은 **지금 쓰는 브라우저에만** 저장됩니다. 다른 PC 에서 열면 다시 보입니다.',
        },
      ],
    },
    {
      id: 'lists',
      title: '화면 현황 · 콘텐츠 유형 · 최근 활동',
      blocks: [
        {
          kind: 'actions',
          title: '카드가 보여주는 것',
          items: [
            { label: '화면 현황', text: '화면을 최대 8대까지, **문제가 큰 순서**(오류 → 오프라인 → 미배정 → 정상)로 보여줍니다. 줄을 누르면 화면 상세로 갑니다.' },
            { label: '콘텐츠 유형', text: '이미지·영상·웹·템플릿이 각각 몇 개인지 도넛 그림으로 보여줍니다.' },
            { label: '곧 끝나는 콘텐츠', text: '7일 안에 게시가 끝나는 콘텐츠. 없으면 “7일 안에 게시가 끝나는 콘텐츠가 없습니다” 라고 나옵니다.' },
            { label: '최근 활동', text: '최근에 누가 무엇을 바꿨는지 8건. ‘감사로그 조회’ 권한이 있는 계정에만 내용이 보입니다.' },
            { label: '전체 보기', text: '각 구역의 전체 목록 화면으로 갑니다.' },
          ],
        },
      ],
    },
  ],
};

export const DISPLAYS: ManualChapter = {
  slug: 'displays',
  title: '화면',
  summary: '등록한 화면을 표로 보고, 새 화면을 등록하고, 여러 대에 편성을 한꺼번에 배정합니다.',
  menuPath: '/displays',
  editCapability: 'fleet:write',
  sections: [
    {
      id: 'read',
      title: '목록 읽기',
      blocks: [
        {
          kind: 'text',
          text: '이름 앞의 **초록 점**은 켜져 있는 화면, **회색 점**은 꺼져 있거나 연결이 끊긴 화면입니다. 화면을 끈 뒤 회색이 되기까지 약 1분 30초 걸립니다. 맨 위 한 줄 요약에 “오프라인 N”, “배정 없음 N” 처럼 문제가 모여 보입니다.',
        },
        {
          kind: 'text',
          text: '**그룹** 열은 그 화면이 든 화면 그룹입니다 — 그룹 편성·캠페인 대상·담당 범위가 모두 이 그룹을 씁니다. 그룹에 들지 않은 화면은 “—” 이고, 그룹을 하나도 만들지 않았으면 이 열은 나오지 않습니다. 그룹 하나만 보려면 [[필터]] 의 **그룹** 에서 고르세요.',
        },
        {
          kind: 'text',
          text: '**남은 공간** 열은 그 기기가 콘텐츠를 더 내려받을 수 있는 여유 용량입니다. 1GB 아래(또는 전체의 10% 아래)면 노랗게 표시되므로, 큰 영상을 보내기 전에 이 열을 확인하세요. 공간이 부족해지면 기기가 받아 둔 파일을 자동으로 지워 재생이 예고 없이 비게 됩니다. “—” 는 **그 기기가 용량을 보고하지 않았다**는 뜻이지 공간이 없다는 뜻이 아닙니다(에이전트 0.6.7 이상이 보고합니다). 자세한 용량과 그 값의 출처는 화면 상세의 “캐시” 줄에 있습니다.',
        },
        {
          kind: 'legend',
          title: '이름·버전 옆에 붙는 표시',
          items: [
            { label: '바로 보내기', tone: 'brand', meaning: '편성 대신 임시로 보낸 콘텐츠가 재생 중입니다.' },
            { label: '숨김', tone: 'neutral', meaning: '목록에서 숨겨 둔 화면입니다. 재생은 계속됩니다.' },
            { label: 'canary', tone: 'neutral', meaning: '새 에이전트 버전을 먼저 받는 화면입니다.' },
            { label: 'v0.5.3 고정', tone: 'warn', meaning: '에이전트 버전이 고정되어 새 버전이 나와도 올라가지 않습니다.' },
            { label: '정비 모드', tone: 'warn', meaning: '현장에서 정비 모드를 열어 둔 상태입니다. 재생이 멈춰 있고, 기기 화면 오른쪽 위의 **×** 를 눌러야 풀립니다.' },
            { label: '0.7.20 설치 예약', tone: 'warn', meaning: '새 버전을 받을 차례지만 공급사가 정한 설치 시간대를 기다리는 중입니다. 시간대가 열리면 스스로 설치합니다(“에이전트 업데이트” 장).' },
            { label: '0.7.20 확인 대기', tone: 'danger', meaning: '기기가 설치 확인을 요구해 스스로 설치하지 못했습니다. 현장에서 정비 모드의 [[지금 업데이트]] 를 누릅니다.' },
          ],
        },
        {
          kind: 'actions',
          title: '목록 위의 도구',
          items: [
            { label: '이름·위치·디바이스 ID·MAC 검색', text: '화면 이름이나 위치로 찾습니다. 현장에서 정비 모드 화면에 뜬 디바이스 ID 를 알려 주면 그 번호로도 찾습니다. 뒤 6자리처럼 일부만 넣어도 됩니다. 공유기 DHCP 표에서 본 **MAC**(`C1:02:3F` 처럼 일부, 하이픈 표기도)으로도 찾습니다.' },
            { label: '전체 상태', text: '켜진 화면 또는 꺼진 화면만 봅니다.' },
            { label: '전체 위치', text: '[[필터]] 안에 있습니다. 한 지점의 화면만 봅니다. **위치 미지정** 을 고르면 위치를 적지 않은 화면만 모입니다 — 지점별로 정리할 때 먼저 확인할 목록입니다. 위치는 표에 별도 열로 있습니다.' },
            { label: '필터', text: '위치(위 “전체 위치” 와 같은 고르개)와 숨긴 화면(숨김 제외 · 숨김 포함 · 숨김만)을 고릅니다. 걸려 있는 거르개 수가 단추에 붙습니다. 지금 보이는 목록의 CSV 는 옆의 [[…]] ▸ [[CSV 내려받기]] 로 받습니다.' },
          ],
        },
        {
          kind: 'text',
          text: '**열 이름을 누르면** 그 열로 정렬하고, 한 번 더 누르면 반대로 정렬합니다(이름·위치·그룹·마지막 신호·남은 공간·버전). 처음 누르면 손볼 화면이 위로 옵니다 — **마지막 신호** 는 오래된 순, **남은 공간** 은 적은 순, **버전** 은 낮은 버전 순입니다. 정렬은 이 브라우저에 기억됩니다. **편성**·**버전** 열 이름 옆의 필터 아이콘을 누르면 그 열의 값과 대수가 나오고, 체크한 값의 화면만 남습니다. 필터를 걸면 표 위에 “필터 결과 N대 · 전체 N대” 와 [[필터 해제]] 가 붙고, 필터에 가려진 화면은 선택에서도 빠집니다 — 일괄 작업은 보이는 화면에만 적용됩니다. 목록 위의 [[CSV]] 는 지금 보이는 목록(필터·정렬 그대로)을 엑셀에서 열 수 있는 파일로 내려받습니다 — 현장 점검표나 자산 대장에 붙여 쓰세요.',
        },
      ],
    },
    {
      id: 'register',
      title: '새 화면 등록하기',
      blocks: [
        {
          kind: 'text',
          text: '기기에 에이전트를 설치하고 실행하면 기기 화면에 **6자리 코드**가 크게 뜹니다. 그 코드를 콘솔에 넣는 것이 등록입니다. 설치 방법은 “화면 등록하기” 장을 보세요.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[화면]] 메뉴에서 [[화면 추가]] 를 누릅니다.' },
            { title: '창 맨 위 “등록 대기 중인 화면” 에서 그 기기를 누르거나, “등록 코드” 에 기기 화면에 보이는 코드를 넣습니다.', body: '등록 대기 목록에는 지금 코드를 띄우고 있는 기기가 종류·해상도·IP 와 함께 나옵니다 — 사무실에서 현장의 기기를 가려 고를 수 있습니다. 코드를 직접 넣을 때는 소문자로 넣어도 대문자로 바뀝니다.' },
            { title: '“화면 이름” 과 “위치” 를 정합니다.', body: '이름은 “본사 1층 로비” 처럼 현장에서 찾을 수 있게 짓습니다. 위치는 목록에서 고르거나 [[새 위치 직접 입력…]] 으로 새로 적습니다.' },
            { title: '[[등록]] 을 누릅니다.', body: '기기 화면이 “등록되었습니다” 로 바뀌고, 목록에 새 화면이 나타납니다.' },
            { title: '이어서 이 화면에 편성을 배정합니다.', body: '배정하기 전에는 기기에 “편성이 배정되지 않았습니다” 가 나옵니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '“코드가 만료되었습니다” 가 뜨면',
          text: '코드는 15분마다 새로 바뀝니다. 기기 화면에 지금 떠 있는 새 코드를 다시 넣으세요.',
        },
      ],
    },
    {
      id: 'assign',
      title: '편성 배정하기 — 이 화면에서 무엇을 재생할지',
      blocks: [
        {
          kind: 'text',
          text: '화면에는 **플레이리스트** 하나(항상 같은 것) 또는 **스케줄** 하나(시간대마다 다른 것)를 배정합니다.',
        },
        {
          kind: 'steps',
          title: '화면 한 대에 배정하기',
          steps: [
            { title: '목록에서 화면 이름을 눌러 화면 상세로 들어갑니다.' },
            { title: '오른쪽 “편성 배정” 목록에서 플레이리스트나 스케줄을 고릅니다.', body: '고르는 **즉시 저장**됩니다. 보통 30초 안에 화면이 바뀝니다.' },
          ],
        },
        {
          kind: 'steps',
          title: '여러 대에 한꺼번에 배정하기',
          steps: [
            { title: '목록 왼쪽 체크박스로 화면들을 고릅니다.', body: '맨 위 체크박스는 지금 보이는 화면을 모두 고릅니다.' },
            { title: '위에 나타난 막대에서 [[편성 선택…]] 을 눌러 플레이리스트나 스케줄을 고릅니다.' },
            { title: '[[일괄 배정]] 을 누릅니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '편성 칸에 “· 그룹 편성” 이나 “없음” 이 보이면',
          text: '그 화면에는 따로 배정한 편성이 없다는 뜻입니다. “(이름) · 그룹 편성” 은 옆 **그룹** 열의 그룹에 걸린 편성을 따르고 있다는 뜻이고, “없음” 이면 그룹에도 편성이 없거나 그룹에 들지 않아 아무것도 재생되지 않습니다. [[CSV]] 에는 이때 “그룹 상속 / 없음” 으로 적힙니다.',
        },
      ],
    },
    {
      id: 'bulk',
      title: '여러 대를 한꺼번에 다루기',
      blocks: [
        {
          kind: 'actions',
          title: '화면을 고르면 나타나는 막대',
          items: [
            { label: '일괄 배정', text: '고른 화면 모두에 같은 편성을 배정합니다.' },
            { label: '업데이트 채널…', text: '둘째 줄([[기타 일괄 변경]])에 있습니다(위치 적용·소리·그룹·정비 PIN 도 같은 줄). 고른 화면을 새 버전을 먼저 받는 카나리 채널이나 안정 채널로 지정합니다. **고르는 즉시** 적용됩니다.' },
            { label: '위치 적용', text: '고른 화면들을 같은 위치로 옮깁니다.' },
            { label: '소리…', text: '둘째 줄에서 음소거·음소거 해제를 고르면 고른 화면 모두에 **곧바로** 적용됩니다. 음량은 화면 상세의 [[화면 설정]] ▸ [[화면 표시]] 에서 한 대씩 정합니다.' },
            { label: '그룹 적용', text: '둘째 줄에서 “그룹 선택…” 으로 그룹을 고른 뒤 누르면 고른 화면들이 그 그룹에 들어갑니다(“그룹에서 빼기” 를 고르면 뺍니다). 그룹은 [[화면 그룹]] 메뉴에서 만듭니다.' },
            { label: '정비 PIN…', text: '고른 화면 모두에 같은 정비 모드 PIN 을 정합니다. 화면 관리 권한이 있는 계정에만 보입니다.' },
            { label: '바로 보내기', text: '고른 화면 모두에 콘텐츠를 임시로 띄웁니다.' },
            { label: '숨기기', text: '목록에서 숨깁니다(재생은 계속됩니다).' },
            { label: '삭제', text: '막대 오른쪽의 [[…]] 안에 있습니다. 등록을 없앱니다. 되돌릴 수 없습니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '[[위치 적용]] 을 누르기 전에 위치를 골랐는지 확인하세요',
          text: '위치를 고르지 않은 채(“위치 미지정”) 누르면 고른 화면들의 위치가 **모두 지워집니다.**',
        },
      ],
    },
    {
      id: 'groups',
      title: '화면 그룹 — 사옥·지점 단위로 묶기',
      blocks: [
        {
          kind: 'text',
          text: '왼쪽 메뉴 [[화면]] 바로 아래 [[화면 그룹]] 에서 화면 묶음을 만들고 봅니다. 그룹은 세 곳에 쓰입니다 — ① **그룹 편성**: 그룹의 화면 가운데 따로 배정한 편성이 없는 화면에 나갑니다. ② **캠페인·긴급 송출의 대상**. ③ 사옥·지점 담당자의 **담당 범위**(“관리” 장). 목록의 줄마다 들어 있는 화면 수, 그룹 편성, 그 그룹을 대상으로 고른 캠페인, 그 그룹을 맡은 계정(계정 관리 권한이 있을 때)이 함께 보입니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[화면 그룹]] 메뉴 위쪽 칸에 이름(예: 판교 사옥)을 적고 [[그룹 만들기]] 를 누릅니다.', body: '만들기·지우기는 화면 관리 권한이 있고 담당 범위가 없는 관리자가 합니다.' },
            { title: '[[화면]] 목록에서 그 사옥의 화면을 체크하고, 막대 둘째 줄의 그룹에서 방금 만든 그룹을 고른 뒤 [[그룹 적용]] 을 누릅니다.', body: '위치로 걸러 놓고 머리의 체크칸으로 고르면 빠릅니다.' },
            { title: '필요하면 [[화면 그룹]] 의 줄 오른쪽 연필을 눌러 그룹 편성(플레이리스트·스케줄)을 고르고 [[저장]] 을 누릅니다.' },
          ],
        },
        {
          kind: 'text',
          text: '줄의 화면 수(“12대”)를 누르면 그 그룹의 화면만 걸러진 화면 목록이 열립니다. 화면 목록의 [[…]] ▸ [[화면 그룹 관리]] 로도 이 메뉴에 옵니다. 화면·캠페인·계정이 쓰고 있는 그룹은 지워지지 않고, 무엇을 먼저 치워야 하는지 알려 줍니다.',
        },
      ],
    },
    {
      id: 'locations',
      title: '사업장·위치 — 지역 › 사업장 › 위치로 정리하기',
      blocks: [
        {
          kind: 'text',
          text: '왼쪽 메뉴 [[화면 그룹]] 아래 [[사업장·위치]] 에서 이 서버의 화면을 **조직** 아래 **지역 › 사업장 › 위치** 세 층으로 정리하고, 위치마다 **주소**를 정합니다. **지역**은 여러 사업장을 묶는 단위, **사업장**은 주소가 있는 한 곳(사옥·공장·매장·지점), **위치**는 그 안에서 화면마다 적은 위치(화면 추가·화면 상세 “기본 정보”·[[기타 일괄 변경]])입니다. **조직 이름**은 맨 위 한 칸이고, **지역·사업장**은 여기서 위치마다 정합니다. 왼쪽 표는 위치마다 **지역 · 사업장 · 주소 · 화면 수(켜짐)** 를, 오른쪽 **구성** 은 그 결과로 묶인 모습을 보여 줍니다 — 고치는 대로 바로 바뀌어 저장하기 전에 어떻게 묶이는지 봅니다. [[모니터링]] 의 그림판(지역·사업장·위치 영역)과 왼쪽 나무(맨 위가 조직 이름), 대시보드의 위치별 화면 현황이 이 구성으로 그려지고, 주소는 다른 서버와 연동된 서버의 **통합 모니터링** 지도에서 노드 카드의 자리가 됩니다. 화면 수를 누르면 그 위치로 거른 [[화면]] 목록이 열리고, 위치를 적지 않은 화면은 맨 아래 **위치 미지정** 줄에 모입니다.',
        },
        {
          kind: 'steps',
          title: '층 이름 고르기 — 업종에 맞는 말로',
          steps: [
            { title: '위쪽 [[층 이름]] 에서 업종에 맞는 묶음을 고릅니다.', body: '**범용**(지역 › 사업장 › 위치, 기본) · **제조**(지역 › 공장 › 건물) · **오피스·공공기관**(지역 › 사옥 › 층) · **유통·프랜차이즈**(권역 › 매장 › 구역) · **병원·학교**(지역 › 캠퍼스 › 건물) 가 있습니다. 고르는 즉시 이 화면의 표 머리와 오른쪽 구성이 그 말로 바뀝니다.' },
            { title: '맞는 묶음이 없으면 **직접 입력** 을 고르고 세 칸에 적습니다.', body: '칸마다 8자까지이고, 비운 칸은 기본(지역 · 사업장 · 위치)으로 돌아갑니다.' },
            { title: '위쪽의 [[저장]] 을 누릅니다.', body: '메뉴 부제와 [[모니터링]] 의 나무·그림판·지도, 사업장 구성 창이 모두 고른 말로 적힙니다. 화면의 글자만 바뀌고 저장된 지역·사업장·위치 값은 그대로입니다 — 이 설명서는 기본 말(지역 › 사업장 › 위치)로 적습니다.' },
          ],
        },
        {
          kind: 'steps',
          title: '조직 이름과 지역·사업장 정하기',
          steps: [
            { title: '위쪽 [[조직 이름]] 에 회사·기관 이름을 적습니다(예: 한국전자).', body: '[[모니터링]] 왼쪽 나무의 맨 위에 이 이름이 나오고, 누르면 전체를 봅니다. 비워 두면 “전체” 입니다.' },
            { title: '**기본 지역** · **기본 사업장** 에 이 서버의 기본값을 적습니다(예: 경기 · 판교 사옥).', body: '사업장이 하나뿐이면 이것만 적으면 됩니다. 위치마다 정하지 않은 칸은 이 값을 따르고, 이것도 비우면 지역은 “지역 미지정”, 사업장은 “본 서버” 입니다.' },
            { title: '여러 사업장을 이 서버 하나로 관리하면, 표에서 위치마다 지역·사업장을 적습니다.', body: '한 번 적은 이름은 다른 칸에서 골라 쓸 수 있습니다.' },
            { title: '위쪽의 [[저장]] 을 누릅니다.', body: '저장하지 않은 칸 수가 그 옆에 나오고, [[되돌리기]] 로 처음 값으로 돌립니다. 다른 사람이 먼저 고쳤으면 저장하지 않고 알려 줍니다 — 새로고침한 뒤 다시 고칩니다.' },
          ],
        },
        {
          kind: 'steps',
          title: '주소 정하기 — 통합 모니터링 지도의 자리',
          steps: [
            { title: '위쪽 [[기본 주소]] 칸(이 서버의 기본값)이나 표의 주소 칸을 누릅니다.', body: '주소를 적지 않은 위치는 기본 주소를 따릅니다 — 흐린 글자가 따를 주소입니다. 위치가 한 곳에 모여 있으면 기본 주소 하나로 충분합니다.' },
            { title: '[[주소 검색]] 에 도로명과 건물번호(예: 평택항로 1)·건물명·지번·우편번호를 적고, 찾은 주소를 누릅니다.', body: '검색은 이 서버 안의 주소 DB(행정안전부 도로명주소)에서 합니다 — 인터넷이 없어도 됩니다. 고른 주소에 좌표가 있으면 “지도 위치” 가 초록으로 붙습니다. “좌표 없음” 이면 지도에 서지 않습니다.' },
            { title: '필요하면 [[상세 주소 (동·층·호)]] 를 적고 [[적용]] 을 누른 뒤, 위쪽 [[저장]] 을 누릅니다.', body: '검색되지 않는 주소(해외 등)는 “검색되지 않는 주소는 직접 입력” 으로 우편번호·기본 주소를 적고, 아는 경우 위도·경도도 적습니다. 주소를 비우려면 [[주소 지우기]].' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '주소 검색이 안 될 때 — 주소 DB',
          text: '“이 서버에는 아직 주소 DB 가 없습니다” 가 뜨면 시스템 담당자가 [[시스템]] 의 [[주소 DB]] 에서 행정안전부 주소정보누리집(business.juso.go.kr)의 「도로명주소 한글」(검색)과 「위치정보요약DB」(지도 좌표) zip 을 넣어야 합니다. 100MB 를 넘는 파일은 사내 주소로 콘솔을 열어 올립니다(운영 문서 “모니터링 지도와 주소 DB”).',
        },
        {
          kind: 'actions',
          title: '줄 끝의 단추 — 위치 이름',
          items: [
            { label: '이름 바꾸기', text: '연필 단추입니다. 새 이름을 적고 [[저장]](또는 Enter). 그 위치의 화면 전부(숨긴 화면 포함)와 지역·사업장 지정, 모니터링 배치가 함께 바뀝니다. **이미 있는 이름으로 바꾸면 두 위치가 합쳐집니다** — 한 번 더 물어보고, 지역·사업장과 배치는 이미 있던 위치의 것을 남깁니다. 같은 지점이 두 이름으로 갈렸을 때 씁니다.' },
            { label: '지우기', text: '휴지통 단추입니다. 그 위치의 화면은 지워지지 않고 **위치 미지정**으로 돌아갑니다. 그 위치의 지역·사업장 지정과 모니터링 배치도 함께 지워집니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '알아 둘 것',
          text: '위치는 화면마다 적는 값이라 여기서 빈 위치를 만들지는 않습니다. 조직 이름·층 이름·지역·사업장·주소를 고치던 중에는 이름 바꾸기·지우기가 눌리지 않습니다 — 먼저 [[저장]] 하거나 [[되돌리기]] 하세요. 누구나 볼 수 있고, 조직 이름·층 이름과 지역·사업장·주소는 화면 관리 권한, 위치 이름 바꾸기·지우기는 편성 권한이 있고 **담당 범위가 없는** 계정만 고칩니다(위치와 사업장 구성은 다른 그룹의 화면도 함께 씁니다). 통합 관제로 묶은 원격 서버의 지역·사업장은 [[모니터링]] 도구 줄의 [[사업장 구성]] 에서 정하고, 여기서 저장해도 그 지정은 그대로 남습니다.',
        },
      ],
    },
    {
      id: 'hide-delete',
      title: '숨기기와 삭제의 차이',
      blocks: [
        {
          kind: 'table',
          header: ['', '숨기기', '삭제'],
          rows: [
            ['화면 재생', '계속 재생합니다', '멈추고 등록 전 상태로 돌아갑니다'],
            ['재생 기록', '계속 쌓입니다', '지워집니다'],
            ['되돌리기', '[[숨김 해제]] 로 언제든', '**불가능** — 현장에 가서 다시 등록해야 합니다'],
            ['이럴 때', '철거 예정·장기 수리 중인 화면을 목록에서 뺄 때', '기기를 완전히 없앴을 때'],
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '숨긴 화면을 다시 보려면',
          text: '목록 위 [[필터]] 에서 숨긴 화면을 “숨김만” 으로 바꾸고, 화면을 골라 [[숨김 해제]] 를 누르세요.',
        },
      ],
    },
  ],
};

export const DISPLAY_DETAIL: ManualChapter = {
  slug: 'display-detail',
  title: '화면 상세',
  summary: '화면 한 대가 지금 무엇을 재생 중인지 사진으로 확인하고, 그 편성이 결정된 이유와 위치·회전·업데이트를 관리합니다.',
  menuPath: '/displays',
  icon: SquareMousePointer,
  editCapability: 'schedule:write',
  sections: [
    {
      id: 'open',
      title: '여는 방법',
      blocks: [
        {
          kind: 'text',
          text: '[[화면]] 목록이나 [[모니터링]] 에서 화면을 누르면 열립니다. 주소창의 주소를 그대로 보내면 다른 사람도 같은 화면 상세를 열 수 있습니다.',
        },
      ],
    },
    {
      id: 'now',
      title: '현재 화면',
      blocks: [
        {
          kind: 'text',
          text: '왼쪽 위 큰 그림은 **이 페이지를 연 순간 기기 화면을 찍어 온 사진**입니다. 사진이 도착하기까지 보통 20~40초, 길게는 약 1분 걸립니다. 그동안은 **전에 찍어 둔 사진**(찍은 시각을 함께 적습니다)을, 그것도 없으면 편성 내용으로 그린 그림을 먼저 보여주고, 새 사진이 오면 교체합니다. 그 뒤로는 자동으로 갱신되지 않으므로 최신 모습은 [[다시 캡처]] 로 받으세요.',
        },
        {
          kind: 'legend',
          title: '그림 제목 옆 표시',
          items: [
            { label: '캡처', tone: 'ok', meaning: '기기가 실제로 찍어 보낸 사진입니다. 옆에 찍은 시각이 붙습니다.' },
            { label: '캡처 중', tone: 'info', meaning: '기기가 새 사진을 보내기를 기다리는 중입니다. 그림 오른쪽 위에 “최신 화면 받는 중” 이 함께 뜹니다.' },
            { label: '이전 모습', tone: 'neutral', meaning: '새 사진 대신 전에 찍어 둔 사진을 보여주는 중입니다. 옆에 찍은 시각이 붙습니다. 지금 화면과 다를 수 있습니다.' },
            { label: '편성 기준', tone: 'neutral', meaning: '사진이 없어 편성 내용으로 그린 그림입니다.' },
            { label: '마지막 보고', tone: 'neutral', meaning: '화면이 꺼져 있어 마지막으로 보고된 내용을 보여줍니다.' },
            { label: '편성과 다름', tone: 'warn', meaning: '기기가 재생 중이라고 보고한 콘텐츠가 지금 편성에 없습니다. 편성을 바꾼 직후라면 잠시 기다리세요.' },
          ],
        },
        {
          kind: 'actions',
          title: '그림 주변 단추',
          items: [
            { label: '다시 캡처', text: '지금 기기 화면을 새로 캡처합니다.' },
            { label: '크게 보기', text: '화면 전체로 크게 봅니다. 키보드 ← → 로 넘기고 Esc 로 닫습니다.' },
            { label: '지금으로', text: '아래 시간표에서 다른 시각을 보고 있을 때, 지금 시각으로 돌아옵니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '시간표로 특정 시각의 화면 미리 보기',
          text: '스케줄이 배정된 화면에는 그림 아래에 **오늘 시간표** 막대가 있습니다. 막대의 원하는 시각을 누르면 “선택 시각의 화면” 을 미리 보여줍니다. 막대 아래 [[시간대별로 보기]] 를 누르면 **구간 N개** 목록이 펼쳐지고(다시 누르면 접힙니다), 그 아래 “재생 순서” 목록은 한 번 도는 동안 재생되는 콘텐츠 순서입니다. 카드 머리 오른쪽의 **시각 기준** 은 이 하루를 어느 시간대(타임존)로 계산했는지 나타냅니다 — 화면에 타임존을 정해 두었으면 “화면 설정”, 아니면 “스케줄 기준” 이라고 적힙니다.',
        },
      ],
    },
    {
      id: 'why',
      title: '편성 결정 근거',
      blocks: [
        {
          kind: 'text',
          text: '“현재 화면” 제목 옆의 **동그란 물음표**를 누르면 열립니다. 위쪽에 편성이 정해지는 순서(**긴급 오버라이드 → 바로 보내기 → 편성(화면 배정, 없으면 그룹 상속) → 캠페인**)를 적어 두고, 그 아래에 이 화면의 판정 결과를 한 줄씩 보여줍니다 — ✓ 가 실제로 적용된 단계입니다. 스케줄을 바꿨는데 화면이 그대로일 때 확인하면, 대부분 캠페인이나 바로 보내기가 위에 걸려 있습니다.',
        },
      ],
    },
    {
      id: 'settings',
      title: '위치·편성·회전·업데이트',
      blocks: [
        {
          kind: 'actions',
          title: '오른쪽 열에서 고칠 수 있는 것',
          items: [
            { label: '이름·위치·태그', text: '“기본 정보” 에는 값만 보입니다. 고치려면 줄 오른쪽의 **연필 단추**를 누르고, 바꾼 뒤 [[저장]] 을 누릅니다(입력칸에서는 Enter). [[취소]] 나 Esc 를 누르면 그대로 둡니다.' },
            { label: '위치', text: '등록된 위치 목록에서 고르고, 없는 위치는 [[새 위치 직접 입력…]] 으로 적습니다. 모니터링에서 같은 위치끼리 묶입니다.' },
            { label: '태그', text: '캠페인 대상을 고를 때 쓰는 이름표. [[태그 추가…]] 목록에서 다른 화면에 쓰이는 태그를 고르고, 없는 태그는 [[새 태그 직접 입력…]] 으로 적습니다(Enter 로 붙이고, 쉼표로 여러 개를 한 번에). × 로 뺍니다. 모두 [[저장]] 을 눌러야 저장됩니다.' },
            { label: '그룹', text: '[[화면 그룹]] 메뉴(또는 연동 시스템)에서 만든 그룹에 이 화면을 넣고 뺍니다. 화면에 따로 배정한 편성이 없으면 그룹의 편성을 따릅니다.' },
            { label: '캐시', text: '이 기기가 콘텐츠 파일로 쓴 용량과 **남은 공간**입니다. 여유가 1GB 아래이거나 전체의 10% 아래로 떨어지면 노랗게 표시됩니다 — 공간이 부족해지면 기기가 받아 둔 파일을 자동으로 지워 재생이 예고 없이 비게 됩니다. 아래 작은 글씨가 그 값의 출처입니다: “기기 저장소 … 중” 은 실제 디스크 여유이고, “웹뷰 할당량 … 기준” 은 브라우저가 허락한 양이라 디스크 여유와 다를 수 있습니다. “보고받지 못했습니다” 는 에이전트가 옛 버전이라는 뜻입니다(0.6.7 이상이 보고합니다).' },
            { label: '편성 배정', text: '이 화면에서 재생할 플레이리스트·스케줄을 고릅니다.' },
            { label: '기기 정보', text: '“기본 정보” 아래 **기기 정보** 를 펼치면 기기를 볼 때의 순서로 값이 섭니다 — 기기·디바이스 ID·등록 → 해상도·방향·타임존 → 망·IP·MAC → **CPU·메모리·온도·가동 시간**. 맨 아래 **기기 로그** 에서 그 화면의 최근 로그를 받아 봅니다. 화면이 느리거나 끊길 때 현장에 가지 않고 먼저 보는 자리입니다(에이전트 0.7.17 부터 보냅니다 — 기기·OS 가 읽게 해 주지 않는 칸은 “—”).' },
            { label: '정비 모드 해제', text: '현장에서 정비 모드를 연 채 두면 재생이 멈춥니다. 그때 화면 상세 위쪽에 “정비 모드가 열려 있어 재생이 멈춰 있습니다” 가 뜨고, [[정비 모드 해제]] 를 누르면 원격으로 재생에 돌아갑니다(에이전트 0.7.17 부터, 화면 관리 권한).' },
          ],
        },
        {
          kind: 'text',
          text: '나머지는 오른쪽 위 **톱니바퀴** 를 눌러 여는 **화면 설정** 창에 있습니다. 한 번 정하면 한동안 바꾸지 않는 항목이라, 상세 화면에서 자리를 빼고 창으로 모았습니다.',
        },
        {
          kind: 'actions',
          title: '화면 설정 창의 메뉴',
          items: [
            { label: '소프트웨어', text: '지금 에이전트 버전과 업데이트 상태, **업데이트 채널**(안정·카나리 — 카나리는 새 버전을 먼저 받음), **버전 고정**(맨 아래 [[고급]] 을 펼쳐서 — 이 화면만 특정 버전에 묶기, 고정돼 있으면 펼쳐진 채로 열립니다), **자동 업데이트**(기기에 맡김 · 켜기 · 끄기).' },
            { label: '화면 표시', text: '**화면 회전** 0° · 90° · 180° · 270° — 모니터를 세로로 세워 달았을 때 고릅니다. 글자가 거꾸로 보이면 90°와 270° 중 반대쪽. **운영 시간** — “환경 설정 따르기”(기본) · “늘 켜기”(24시간 로비처럼 쉬지 않는 화면) · “이 화면만”(이 화면에만 다른 시간대를 적고 [[저장]]). **소리** — 영상의 음량과 음소거. 콘텐츠마다 정한 소리 크기와 곱해지고 기기·TV 음량보다 크게는 나오지 않습니다 — 소리가 나면 안 되는 로비는 음소거로 둡니다.' },
            { label: '전원·시작', text: '**부팅 시 자동 실행**(기기에 맡김 · 켜기 · 끄기)과 원격 명령 [[기기 재부팅]] 과, 그 옆 [[…]] 안의 [[앱 종료]] · [[전원 종료]]. **앱 종료 · 전원 종료는 콘솔에서 다시 켤 수 없습니다**(현장에서 켜야 합니다).' },
            { label: '정비 PIN', text: '현장에서 정비 모드를 열 때 넣는 PIN 을 새로 정합니다. 두 번 넣고 [[PIN 저장]]. 정해 둔 PIN 은 다시 볼 수 없습니다. [[콘솔 PIN 해제]] 를 누르면 콘솔에서 정한 PIN 을 지워, 기기에 설정된 PIN(없으면 0000)으로 돌아갑니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '목록에서 고르는 순간 저장됩니다',
          text: '편성 배정·화면 회전·채널·버전 고정·자동 업데이트·부팅 시 자동 실행은 따로 저장 단추가 없습니다. 잘못 골랐으면 다시 올바른 것을 고르세요. 운영 시간의 “이 화면만” 은 시간대를 적고 [[저장]] 을 눌러야 저장됩니다.',
        },
      ],
    },
    {
      id: 'files',
      title: '다운로드된 콘텐츠',
      blocks: [
        {
          kind: 'text',
          text: '이 기기가 재생하려고 갖고 있는 콘텐츠입니다. 줄마다 **콘텐츠 이름 · 크기 · 받은 때 · 출력 예정 · 마지막 출력** 이 적히고, 지금 재생 중인 콘텐츠에는 “지금 편성” 표시가 붙습니다. 기기가 내려받은 파일(이미지·영상·HTML)이 먼저 오고, 그 아래에 **템플릿·웹** 콘텐츠가 옵니다 — 템플릿은 내용이 편성에 실려 와 **기기에 함께 저장되므로**(서버에 닿지 않거나 다시 켜도 그대로 나갑니다) 크기 칸이 “편성에 포함” 이고, 웹은 외부 주소를 그때그때 열어 “외부 주소” 입니다. 둘 다 따로 받은 파일이 없어 고르는 칸이 흐리게 나옵니다(마우스를 올리면 까닭이 보입니다). **반반 화면·구역 나누기 템플릿에 넣은 이미지·영상·HTML** 은 기기가 그 템플릿을 보여 주려고 받아 둔 파일이라 그 템플릿 줄 아래에 묶여 나오고, 템플릿 줄을 고르면 그 파일들을 함께 고릅니다(템플릿에 넣은 파일을 못 받았으면 표 아래에 “「템플릿 이름」 안” 으로 적힙니다). 템플릿 머리글의 **회사 로고**와 **사내 글꼴**(환경 설정의 브랜드)은 기기가 따로 받아 두는 파일이라 “템플릿 로고” · “템플릿 글꼴” 표시와 함께 나오고, 템플릿이 늘 쓰므로 지울 수 없습니다. 편성에 있는데 기기가 아직 갖고 있지 않은 것(“못 받음” · “나갈 때 받음” 등)은 표 아래에 따로 적힙니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '카드는 펼친 채로 열립니다',
          text: '카드 이름을 누르면 접힙니다. 접어 둔 상태에서도 머리줄에 **파일 수와 크기 · 템플릿·웹 수**, 그리고 **빨간 “못 받음 N”** 이 남습니다. “못 받음” 은 지금 재생해야 하는데 기기가 그 파일을 갖고 있지 않다는 뜻이고(내려받다 실패했거나 캐시에서 사라진 경우), 펼치면 어느 콘텐츠인지 표 아래에 표시됩니다. 목록이 비어 있으면 **편성이 배정되지 않았거나 편성이 비어 있다**는 뜻입니다.',
        },
        {
          kind: 'steps',
          title: '특정 콘텐츠만 깨져서 재생되지 않을 때 — 다시 내려받게 하기',
          steps: [
            { title: '목록에서 문제 되는 파일을 체크합니다.' },
            { title: '위의 빨간 “N개 지우기” 단추를 누르고 확인합니다.', body: '콘텐츠 자체는 지워지지 않고, **이 기기가 갖고 있던 사본만** 지웁니다.' },
            { title: '지금 편성에 쓰이는 파일이면 기기가 곧바로 새로 내려받습니다.', body: '꺼져 있는 기기는 다시 켜질 때 지우며, 하루 안에 켜지지 않으면 지시가 없어집니다.' },
          ],
        },
      ],
    },
    {
      id: 'history',
      title: '변경 이력·숨기기·삭제',
      blocks: [
        {
          kind: 'actions',
          title: '맨 위 단추',
          items: [
            { label: '목록으로', text: '왼쪽 위 화살표. **뒤로가기**입니다 — 모니터링에서 들어왔으면 [[모니터링으로]], 대시보드에서 들어왔으면 [[대시보드로]] 라고 적히고 보던 자리(스크롤·필터)로 돌아갑니다. 주소를 직접 열었을 때만 화면 목록으로 갑니다.' },
            { label: '새로고침', text: '상태·사진·다운로드된 콘텐츠 목록을 다시 받습니다.' },
            { label: '변경 이력', text: '이 화면에 누가 무엇을 바꿨는지 최근 50건을 봅니다. ‘감사로그 조회’ 권한이 있는 계정에만 보입니다.' },
            { label: '화면 설정', text: '글자 없이 **톱니바퀴** 하나로 있습니다. 누르면 “화면 설정” 창이 열립니다 — 왼쪽 메뉴 소프트웨어 · 화면 표시 · 전원·시작 · 정비 PIN. 열 수 있는 메뉴가 하나도 없는 계정에는 단추가 나오지 않습니다.' },
            { label: '숨기기', text: '목록에서만 숨깁니다. 재생과 기록은 계속됩니다.' },
            { label: '삭제', text: '맨 위 오른쪽 [[…]] 안에 있습니다. 등록을 없앱니다. **되돌릴 수 없고** 현장에서 다시 등록해야 합니다.' },
          ],
        },
      ],
    },
  ],
};

export const DIRECT_SEND: ManualChapter = {
  slug: 'direct-send',
  title: '바로 보내기',
  summary: '편성을 건드리지 않고, 고른 화면에 사진·영상·템플릿·웹을 지금 바로 띄웁니다. 해제하면 원래대로 돌아옵니다.',
  menuPath: '/displays',
  icon: Send,
  editCapability: 'schedule:write',
  sections: [
    {
      id: 'when',
      title: '언제 쓰나',
      blocks: [
        {
          kind: 'text',
          text: '“오늘 오후 엘리베이터 점검 안내를 로비 화면에 한 시간만” 처럼 **급하고 짧은 일**에 사용합니다. 플레이리스트·스케줄을 만들 필요가 없고, 기간이 끝나거나 해제하면 원래 재생하던 편성으로 자동으로 돌아갑니다.',
        },
      ],
    },
    {
      id: 'send',
      title: '보내기',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '화면 상세 오른쪽 **“편성 배정”** 카드의 [[바로 보내기]] 를 누릅니다.', body: '평소 편성을 정하는 자리와 임시로 덮는 자리가 나란히 있습니다. 여러 대에 보내려면 화면 목록에서 체크한 뒤 막대의 [[바로 보내기]] 를 누릅니다.' },
            { title: '보낼 것을 고릅니다.', body: '사진·영상·PDF 파일을 창에 끌어다 놓거나 [[파일 선택]] 으로 올립니다(PDF 는 쪽마다 이미지가 되어 쪽 순서대로 골라집니다). 기존 콘텐츠를 체크해도 됩니다 — 목록 위의 [[전체]] [[사진]] [[동영상]] [[템플릿]] [[웹]] 으로 종류를 나눠 볼 수 있습니다. 만들어 둔 플레이리스트를 통째로 보내려면 창 맨 위에서 [[플레이리스트]] 를 누르고 하나를 고릅니다 — 켜 둔 항목이 그 순서·노출 시간·전환 그대로 나가고, 보낸 뒤 플레이리스트를 고쳐도 이 바로 보내기는 그대로입니다(다시 보내면 반영).' },
            { title: '사진이면 “한 장당 노출 시간(초)” 을 정합니다.', body: '영상은 길이만큼 재생됩니다. 플레이리스트로 보내면 이 칸이 없고 플레이리스트에 정한 노출 시간을 씁니다.' },
            { title: '“재생 기간” 을 고르고, 필요하면 [[메모]] 칸에 이유를 남깁니다.', body: '화면이 꺼져 있으면 “꺼진 화면에도 걸어 두기” 를 체크해 둘 수 있습니다 — 다시 연결되면(끝낼 시각 전이면) 나갑니다.' },
            { title: '[[보내기]] 를 누릅니다.', body: '화면 상세 맨 위에 “바로 보내기 재생 중” 띠가 나타나고, 목록·모니터링에는 “바로 보내기” 표시가 붙습니다. 꺼진 화면에 걸어 둔 것은 화면이 받아 가기 전까지 띠가 “바로 보내기 · 전달 대기” 로 보이고, 받아 가면 “바로 보내기 재생 중” 으로 바뀝니다.' },
          ],
        },
        {
          kind: 'table',
          header: ['재생 기간', '어떻게 끝나나'],
          rows: [
            ['1시간 · 4시간 · 오늘 자정까지', '시간이 되면 자동으로 원래 편성으로 돌아갑니다'],
            ['직접 지정', '고른 날짜·시각에 자동으로 돌아갑니다'],
            ['해제할 때까지', '해제하기 전까지 **계속** 재생됩니다'],
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '해제를 잊으면 원래 편성이 계속 가려집니다',
          text: '“해제할 때까지” 로 보내면 며칠이고 그대로 남습니다. 끝나는 때를 알고 있다면 시간을 정해 두는 편이 안전합니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '영상은 끝까지 재생됩니다',
          text: '영상도 사진·템플릿·웹과 섞어 보낼 수 있습니다. “한 장당 노출 시간” 은 영상이 아닌 것에만 쓰이고, 영상은 끝까지 재생된 뒤 다음으로 넘어갑니다. 영상만 고르면 노출 시간 칸이 사라집니다. 여기서 올린 파일은 콘텐츠 목록에도 남습니다.',
        },
      ],
    },
    {
      id: 'stop',
      title: '해제하기',
      blocks: [
        {
          kind: 'text',
          text: '화면 상세 맨 위 “바로 보내기 재생 중” 띠의 [[해제]] 를 누르면 곧바로 원래 편성으로 돌아갑니다.',
        },
      ],
    },
  ],
};

export const MONITORING: ManualChapter = {
  slug: 'monitoring',
  title: '모니터링',
  summary: '이 서버 화면의 가동 상태를 조직 › 지역 › 사업장 › 화면 나무와 그림판(지역·사업장·위치 영역 안의 썸네일)으로 좁혀 가며 봅니다. 다른 서버와 연동된 서버의 화면까지 한 지도에 보는 것은 [통합 모니터링] 메뉴입니다.',
  menuPath: '/wall',
  sections: [
    {
      id: 'sites',
      title: '사업장 현황 — 그림판',
      blocks: [
        {
          kind: 'text',
          text: '모니터링 화면은 **본문을 여백 없이 꽉 채운 점 모눈 그림판**이고, **이 서버의 화면**을 그립니다(다른 서버와 연동된 서버는 왼쪽 메뉴 [[통합 모니터링]] 에서 원격 서버까지 한 지도에 봅니다). 그 위에 왼쪽 **사업장 나무**와 위쪽 도구 줄이 떠 있습니다. 나무의 맨 위는 **조직 이름**(정하지 않았으면 “전체”)이고 — 누르면 전체를 봅니다 — 그 아래에 **지역 › 사업장 › 화면**이 층마다 펼치고 접는 목록으로 섭니다. 그림은 **모든 지역을 한 장에 그려 두고 끌어서 옮기고 키우고 줄이며** 봅니다 — 빈 곳을 끌면 이동, **마우스 휠·트랙패드 핀치**(휴대폰은 두 손가락)로 커서 자리를 붙든 채 확대·축소, 오른쪽 아래의 − + 단추와 [[전체 보기]] 도 있습니다(키보드: 화살표 이동, + − 배율, 0 전체 보기). 그림에서 **지역은 점선 상자, 사업장은 그 안의 실선 상자**이고, 사업장 상자 안은 **위치마다 칸**으로 나뉘어 그 위치의 화면이 **썸네일 노드**로 섭니다. 나무의 지역·사업장은 화살표로 접고 펴며, **이름을 누르면 그림이 그 영역으로 날아가 맞춰집니다.** 영역 제목이나 노드를 눌러도 같습니다. 휴대폰처럼 좁은 화면에서는 나무가 위로 올라가고, 조직 이름 옆 화살표로 지역 목록을 접고 폅니다.',
        },
        {
          kind: 'text',
          text: '나무의 줄과 영역 제목 옆에는 **온라인 N / 전체** 와 오프라인 수(붉게), 가동률 막대가 붙습니다 — 막대 색은 전체 온라인(초록) · 일부 오프라인(주황) · 절반 넘게 오프라인(빨강)입니다. 위치 칸 안에서는 꺼진 화면이 앞에 섭니다. 노드의 썸네일은 화면이 보낸 마지막 그림이고, 없으면 편성 내용으로 그립니다 — 여기서는 화면에 새로 찍으라고 하지 않습니다(화면의 지금 모습은 [[화면 상세]] 에서 찍어 봅니다). 화면을 고르면 그 노드에 크게 맞추고 오른쪽(휴대폰은 아래)에 **그 화면의 판**이 열려 그림과 위치·서버·마지막 보고·버전·재생 중인 항목을 적으며, [[화면 상세]] 로 갈 수 있습니다([[×]] 로 닫으면 그 사업장으로). 맨 위 줄에 전체 지역·사업장·화면 수가 적힙니다. 집계는 30초마다 갱신되고, 고른 범위는 주소에 남아 새로 고쳐도 그대로입니다.',
        },
        {
          kind: 'text',
          text: '**위치는 화면마다 입력한 값**입니다(화면 상세 “기본 정보” 의 위치). **나무 맨 위의 조직 이름과 지역·사업장은 [[사업장·위치]] 메뉴에서 정합니다**(도구 줄의 [[사업장 구성]] 으로도 정합니다 — 화면 관리 권한이 있고 담당 범위가 없는 계정에 보이는 단추입니다). 정하기 전에는 조직이 “전체”, 지역이 “지역 미지정”, 사업장이 “본 서버”로 보입니다. [[사업장·위치]] 에서 [[층 이름]] 을 바꿨으면(예: 제조 — 지역 › 공장 › 건물) 나무·그림판·지도와 도구 줄의 숫자도 그 말로 적힙니다 — 이 설명서는 기본 말(지역 › 사업장 › 위치)로 적습니다.',
        },
        {
          kind: 'steps',
          title: '영역 옮기고 크기 바꾸기(배치 편집)',
          steps: [
            { title: '오른쪽 아래의 [[배치 편집]] 을 누릅니다.', body: '지역·사업장·위치 영역마다 점선 윤곽이 생기고, 아래에 “배치 편집 중” 안내가 뜹니다.' },
            { title: '영역의 머리줄(이름이 적힌 줄)을 끌어 옮깁니다.', body: '**사업장은 지역 밖으로, 위치는 사업장 밖으로 나가지 않습니다** — 가장자리에서 멈춥니다. 지역은 그림 어디로든 옮길 수 있습니다. 옮긴 영역은 아래의 다른 영역과 겹칠 수 있고, 손대지 않은 위치는 아래로 비켜 섭니다.' },
            { title: '오른쪽 아래 모서리를 끌어 크기를 바꿉니다.', body: '상위 영역의 안쪽 끝까지 키울 수 있고, 줄일 때는 안에 든 것(사업장·위치, 위치면 화면 노드)을 담을 크기에서 멈춥니다. 위치 칸의 폭을 줄이면 화면 노드가 줄을 바꿔 그만큼 높아집니다.' },
            { title: '[[편집 완료]] 를 누릅니다.', body: '배치는 손을 뗄 때마다 **내 계정에만** 저장됩니다 — 다른 사람의 그림은 그대로입니다. 키보드로는 이름에 초점을 두고 Alt+화살표로 옮기고, Alt+Shift+화살표로 크기를 바꿉니다. 처음 모습으로 돌리려면 편집 중에 [[기본 배치로]] 를 누릅니다.' },
          ],
        },
      ],
    },
  ],
};

/**
 * 통합 모니터링 — 2026-10-28 사용자 지시로 모니터링의 [그림판 | 통합 모니터링] 전환에서 메뉴를 나눴다("통합 관리 서버가 적용되어
 * 있으면 관리자 권한의 계정에도 통합 모니터링 메뉴가 보여야 한다. 메뉴 나누어서"). 글은 모니터링 장의 “통합 모니터링” 절이었다.
 */
export const INTEGRATED_MONITORING: ManualChapter = {
  slug: 'integrated-monitoring',
  title: '통합 모니터링',
  summary: '통합 관제에 묶은 다른 SignageOne 서버의 화면까지 이 서버의 화면과 한 지도에 봅니다. 원격 서버가 묶여 있으면 관리자에게 메뉴가 보입니다.',
  menuPath: '/integrated',
  capability: 'feature:hub_monitor',
  sections: [
    {
      id: 'who',
      title: '누구에게 보이나',
      blocks: [
        {
          kind: 'text',
          text: '통합 관제 모듈이 켜져 있고 **원격 서버가 하나라도 등록된 서버**에서, 화면 관리 권한이 있는 계정(관리자)에게 왼쪽 메뉴 [[통합 모니터링]] 이 보입니다. 원격 서버를 등록·수정하고 그 서버의 화면에 명령을 보내는 [[통합 관제]] 메뉴는 따로이고, 공급사가 고객사에 공개해 둔 서버에서만 관리자에게 보입니다 — 공개하지 않았어도 공급사가 묶어 둔 서버들의 화면은 이 메뉴에서 봅니다. 담당 범위가 정해진 계정에는 보이지 않습니다(다른 서버의 화면이라서).',
        },
      ],
    },
    {
      id: 'map',
      title: '연동된 서버를 한 지도에',
      blocks: [
        {
          kind: 'text',
          text: '**다른 SignageOne 서버와 연동된 서버**(통합 관제에 원격 서버를 등록한 서버)에는 왼쪽 메뉴 [[모니터링]] 바로 아래에 [[통합 모니터링]] 이 생깁니다. 누르면 **이 서버와 원격 서버의 화면을 한 지도에** 그립니다(한반도 지도 — 인터넷 없이 이 서버가 내줍니다). 왼쪽 나무에도 원격 서버의 지역·사업장이 함께 섭니다. [[모니터링]] 은 이 서버의 화면만 그리는 그림판이고, 원격 서버까지 보는 것은 이 메뉴입니다. 연동이 없는 서버에는 이 메뉴가 없습니다.'
        },
        {
          kind: 'text',
          text: '지도 위에는 **화면 노드가 그려집니다** — 화면이 놓인 위치의 주소마다 **노드 카드** 하나가 아래 꼬리로 그 자리를 가리키고, 카드 안에 위치마다 **화면 썸네일**이 섭니다. 카드 테두리 색은 전체 온라인(초록) · 일부 오프라인(주황) · 절반 넘게 오프라인(빨강) · 연결 끊긴 서버(회색 점선)입니다. 카드는 지도와 함께 커지고 작아지고, 전국처럼 멀리 보면 겹치지 않게 **화면 수가 든 점**으로 접힙니다. **점을 누르면 그 카드가 활성이 됩니다** — 제 크기로 펼쳐져 진한 테두리를 두르고 다른 카드 위에 서며, 이제 지도를 키우거나 줄여도 **크기가 바뀌지 않고** 멀리 봐도 점으로 접히지 않습니다(카드 안을 누르거나 화면을 골라도 그 카드가 활성이 됩니다). 지도 배율은 그대로 두고, 카드가 판에 가려지거나 위로 펼칠 자리가 없을 때만 옆으로 옮깁니다. 지도의 빈 곳을 누르거나 Esc 를 누르면 원래대로 돌아갑니다. 빈 곳을 끌면 이동, 마우스 휠·트랙패드 핀치(휴대폰은 두 손가락)로 확대·축소, 오른쪽 아래의 − + 단추와 [[전체 보기]] 도 있습니다. 가장 멀리 줄이면 한반도 둘레의 동아시아가 한눈에 들어오고, 지도 자료가 없는 그 바깥으로는 더 줄이거나 옮겨지지 않습니다(바탕이 잘려 보이지 않게). 나무의 이름이나 카드 머리를 누르면 **지도가 그 자리로 날아갑니다.**',
        },
        {
          kind: 'text',
          text: '통합 모니터링에서 사업장을 고르면 오른쪽(휴대폰은 아래)에 **그 사업장의 판**이 열려 주소와 숫자, **위치마다 화면 썸네일**을 보여 줍니다 — 위치 안에서는 꺼진 화면이 앞에 섭니다. 썸네일은 화면이 보낸 마지막 그림이고, 없으면 편성 내용으로 그립니다 — 여기서는 화면에 새로 찍으라고 하지 않습니다(화면의 지금 모습은 [[화면 상세]] 에서 찍어 봅니다). 지도 카드나 판의 썸네일을 누르면 **그 화면의 판**이 열리고(지도는 키워 둔 배율 그대로 — 그 자리가 판에 가려졌을 때만 옆으로 옮깁니다) 그림과 위치·주소·서버·마지막 보고·버전·재생 중인 항목을 적으며, [[화면 상세]] 로 갈 수 있습니다([[×]] 로 닫으면 그 사업장으로). 맨 위 줄에 전체 지역·사업장·화면 수와 연결이 끊긴 서버 수가 적힙니다. 집계는 30초마다 갱신되고, 고른 범위는 주소에 남아 새로 고쳐도 그대로입니다.',
        },
        {
          kind: 'text',
          text: '**노드 카드의 자리는 화면이 놓인 위치의 주소입니다.** 사업장의 판에서 위치마다, 또는 화면의 판에서 그 화면의 위치에 [[주소 정하기]] 를 누르고 주소를 검색해 고르면 곧바로 저장되고 그 좌표에 노드 카드가 섭니다(이미 정했으면 [[주소 바꾸기]] — 창의 [[주소 지우기]] 로 지웁니다). [[사업장·위치]] 메뉴의 표에서도 같은 주소를 정합니다. 주소를 적지 않은 위치는 서버의 기본 주소를 따르고(판에 “(기본 주소)” 로 흐리게 보입니다), 같은 좌표의 위치들은 한 카드에 모입니다. 멀리서 화면을 고르면 지도가 그 화면의 위치 자리로 들어가고, 이미 카드가 보이는 배율이면 배율을 바꾸지 않습니다. 주소가 없거나 주소에 좌표가 없는 사업장은 지도에 서지 않고 아래 **“주소가 없는 사업장”** 목록에 모입니다(누르면 그 사업장의 판). 주소는 화면 관리 권한이 있고 담당 범위가 없는 계정이 정합니다 — 원격 서버의 위치 주소도 여기서 정해 이 서버에 둡니다. 원격 서버의 지역·사업장은 도구 줄의 [[사업장 구성]] 에서 정합니다 — 정하기 전에는 지역이 “지역 미지정”, 사업장이 그 서버 이름으로 보입니다.',
        },
        {
          kind: 'steps',
          title: '원격 서버까지 지역·사업장·주소 정하기',
          steps: [
            { title: '도구 줄 오른쪽의 [[사업장 구성]] 을 누릅니다.' },
            { title: '서버마다 **기본 지역** · **기본 사업장** 을 적고 [[기본 주소]] 를 고릅니다(예: 경남 · 창원 공장).', body: '“본 서버” 와, 통합 관제에 등록한 원격 서버가 한 칸씩 나옵니다. 원격 서버의 사업장은 여기서 기본 주소를 정해야 지도에 섭니다. 한 번 적은 이름은 다음 칸에서 골라 쓸 수 있습니다.' },
            { title: '한 서버가 여러 사업장을 관리하면 **위치별 개별 지정** 에서 그 위치의 지역·사업장을 적습니다.', body: '비워 둔 칸은 서버 기본값을 따릅니다. 이 서버의 위치별 주소는 [[사업장·위치]] 에서 정합니다.' },
            { title: '[[저장]] 을 누릅니다.', body: '다른 사람이 먼저 고쳤으면 저장하지 않고 알려 줍니다 — 창을 닫았다 다시 열어 고칩니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '망이 다른 사업장의 서버(원격 서버)',
          text: '통합 관제에 등록한 원격 서버의 화면도 같은 나무와 지도에 섭니다. 원격 서버의 화면은 **썸네일 없이 상태(온라인·오프라인)만** 그립니다 — 그 서버의 그림은 망이 달라 이 브라우저가 닿지 않을 수 있습니다. 원격 서버의 위치 이름은 그 서버에서 화면에 입력한 위치라 여기서 고치지 않습니다. **연결이 끊긴 서버는 회색으로, “연결 끊김” 과 함께** 마지막으로 수신한 숫자를 보여 주고 화면 썸네일은 그리지 않습니다 — 화면이 오프라인인 것으로 세지 않습니다. 그 서버 화면에 재부팅 같은 명령을 보내려면 화면을 고른 뒤 [[통합 관제에서 열기]] 로 갑니다 — 통합 관제를 볼 수 있는 계정에만 이 단추가 있습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '지도 바탕이 안 나올 때',
          text: '지도 파일(한반도 상세 약 930MB와 동아시아 저배율 약 22MB)을 아직 넣지 않은 서버는 바탕 없이 노드 카드만 같은 자리에 그리고, 오른쪽 아래에 “지도 타일이 이 서버에 없어…” 라고 알립니다. 지도 파일 설치는 서버 운영 담당자의 일입니다(운영 문서의 “모니터링 지도와 주소 DB”). 지도 아래의 © OpenStreetMap 은 지도 자료의 출처 표기입니다.',
        },
      ],
    },
  ],
};
